'use client'

import { useEffect, useRef, useState } from 'react'

interface SectionDotsProps {
  scrollRef: React.RefObject<HTMLElement | null>
  count: number
}

export function SectionDots({ scrollRef, count }: SectionDotsProps) {
  const [active, setActive] = useState(0)
  const sectionsRef = useRef<HTMLElement[]>([])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    sectionsRef.current = Array.from(container.querySelectorAll('section'))

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = sectionsRef.current.indexOf(entry.target as HTMLElement)
            if (idx !== -1) setActive(idx)
          }
        }
      },
      { root: container, threshold: 0.5 }
    )

    sectionsRef.current.forEach((s) => observer.observe(s))
    return () => observer.disconnect()
  }, [scrollRef])

  const scrollTo = (index: number) => {
    const section = sectionsRef.current[index]
    const container = scrollRef.current
    if (!section || !container) return
    container.scrollTo({ top: section.offsetTop, behavior: 'smooth' })
  }

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-[10px] z-40">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => scrollTo(i)}
          aria-label={`Go to section ${i + 1}`}
          className="flex items-center justify-center w-4 h-4 group"
        >
          <span
            className="block rounded-full transition-all duration-300"
            style={{
              width:  i === active ? 7 : 5,
              height: i === active ? 7 : 5,
              background: 'var(--night)',
              opacity: i === active ? 0.65 : 0.2,
            }}
          />
        </button>
      ))}
    </div>
  )
}
